import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ArrowLeft, 
  Save, 
  Image as ImageIcon,
  Plus,
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper for authenticated fetch
const authFetch = (url, options = {}) => {
  const token = localStorage.getItem('session_token');
  const headers = { ...options.headers };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return fetch(url, { ...options, credentials: 'include', headers });
};

// Convert Google Drive URLs to reliable thumbnail URLs
const convertGoogleDriveUrl = (url) => {
  if (!url) return url;
  if (url.includes('drive.google.com/thumbnail') || url.includes('lh3.googleusercontent.com')) return url;
  
  const fileMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileMatch) return `https://drive.google.com/thumbnail?id=${fileMatch[1]}&sz=w1000`;
  
  const openMatch = url.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/);
  if (openMatch) return `https://drive.google.com/thumbnail?id=${openMatch[1]}&sz=w1000`;
  
  const ucMatch = url.match(/drive\.google\.com\/uc\?(?:export=view&)?id=([a-zA-Z0-9_-]+)/);
  if (ucMatch) return `https://drive.google.com/thumbnail?id=${ucMatch[1]}&sz=w1000`;
  
  return url;
};

export const ArtistEditorPage = () => {
  const navigate = useNavigate();
  const { artistId } = useParams();
  const isNew = artistId === 'new';
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    role: '',
    bio: '',
    image: '',
    imdb: '',
    website: '',
    projects: [],
    companies: []
  });

  // Check auth first
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await authFetch(`${API_URL}/api/auth/me`);
        if (!response.ok) {
          navigate('/cms/login');
          return;
        }
        const userData = await response.json();
        setUser(userData);
      } catch (error) {
        navigate('/cms/login');
      }
    };
    checkAuth();
  }, [navigate]);

  // Fetch data after auth
  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      try {
        // Always fetch projects and companies for the checkboxes
        const [projectsRes, companiesRes] = await Promise.all([
          authFetch(`${API_URL}/api/projects`),
          authFetch(`${API_URL}/api/companies`)
        ]);

        if (projectsRes.ok) setProjects(await projectsRes.json());
        if (companiesRes.ok) setCompanies(await companiesRes.json());

        // Only fetch artist data if editing existing
        if (!isNew) {
          const [artistRes, requestsRes] = await Promise.all([
            authFetch(`${API_URL}/api/artists/id/${artistId}`),
            authFetch(`${API_URL}/api/association-requests`)
          ]);

          if (!artistRes.ok) {
            navigate('/cms');
            return;
          }

          const artistData = await artistRes.json();
          setFormData({
            name: artistData.name || '',
            slug: artistData.slug || '',
            role: artistData.role || '',
            bio: artistData.bio || '',
            image: artistData.image || '',
            imdb: artistData.imdb || '',
            website: artistData.website || '',
            projects: artistData.projects || [],
            companies: artistData.companies || []
          });

          if (requestsRes.ok) {
            const allRequests = await requestsRes.json();
            setMyRequests(allRequests.filter(r => r.artist_id === artistId));
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [artistId, navigate, user, isNew]);

  // Auto-generate slug from name
  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 50);
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData({
      ...formData,
      name,
      slug: formData.slug || generateSlug(name)
    });
  };

  const handleSave = async () => {
    if (!formData.name) {
      alert('Name is required');
      return;
    }

    setSaving(true);
    try {
      const dataToSave = {
        ...formData,
        image: convertGoogleDriveUrl(formData.image),
        slug: formData.slug || generateSlug(formData.name)
      };

      const url = isNew 
        ? `${API_URL}/api/artists`
        : `${API_URL}/api/artists/${artistId}`;
      
      const method = isNew ? 'POST' : 'PUT';

      const response = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSave)
      });

      if (response.ok) {
        navigate('/cms');
      } else {
        const err = await response.json().catch(() => ({}));
        alert(err.detail || 'Failed to save');
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Save failed - please try again');
    } finally {
      setSaving(false);
    }
  };

  // Admin: Toggle project affiliation
  const toggleProject = (projectTitle) => {
    if (user?.role !== 'admin') return;
    const newProjects = formData.projects.includes(projectTitle)
      ? formData.projects.filter(p => p !== projectTitle)
      : [...formData.projects, projectTitle];
    setFormData({ ...formData, projects: newProjects });
  };

  // Admin: Toggle company affiliation
  const toggleCompany = (companyName) => {
    if (user?.role !== 'admin') return;
    const newCompanies = formData.companies.includes(companyName)
      ? formData.companies.filter(c => c !== companyName)
      : [...formData.companies, companyName];
    setFormData({ ...formData, companies: newCompanies });
  };

  const handleRequestAssociation = async (targetType, targetId) => {
    try {
      const response = await authFetch(`${API_URL}/api/association-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_type: targetType,
          target_id: targetId
        })
      });

      if (response.ok) {
        const newRequest = await response.json();
        setMyRequests([...myRequests, newRequest]);
      }
    } catch (error) {
      console.error('Request error:', error);
    }
  };

  // Get available projects/companies (not already associated and no pending request)
  const pendingProjectIds = myRequests.filter(r => r.target_type === 'project' && r.status === 'pending').map(r => r.target_id);
  const pendingCompanyIds = myRequests.filter(r => r.target_type === 'company' && r.status === 'pending').map(r => r.target_id);
  
  const availableProjects = projects.filter(p => 
    !formData.projects.includes(p.title) && !pendingProjectIds.includes(p.project_id)
  );
  const availableCompanies = companies.filter(c => 
    !formData.companies.includes(c.name) && !pendingCompanyIds.includes(c.company_id)
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="editorial-container py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/cms" className="text-xl editorial-serif">Cultural Revolution</Link>
            <span className="text-xs uppercase tracking-widest text-muted-foreground px-2 py-1 bg-muted">
              {isNew ? 'New Artist' : 'Edit Artist'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/cms">
              <Button variant="ghost" size="sm">
                <ArrowLeft size={16} className="mr-2" />
                Back
              </Button>
            </Link>
            <Button onClick={handleSave} disabled={saving}>
              <Save size={16} className="mr-2" />
              {saving ? 'Saving...' : (isNew ? 'Create Artist' : 'Save')}
            </Button>
          </div>
        </div>
      </header>

      <div className="editorial-container py-8">
        <div className="max-w-3xl mx-auto space-y-8">
          {/* Basic Info */}
          <Card className="p-6">
            <h2 className="text-xl editorial-serif mb-4">Profile Information</h2>
            <div className="space-y-4">
              {/* Image URL Input - Google Drive supported */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Photo URL
                  <span className="text-xs text-muted-foreground ml-2">(Use Google Drive share link)</span>
                </label>
                <div className="flex items-start gap-4">
                  <div className="w-32 h-40 bg-muted overflow-hidden flex-shrink-0">
                    {formData.image ? (
                      <img 
                        src={convertGoogleDriveUrl(formData.image)} 
                        alt="Headshot" 
                        className="w-full h-full object-cover" 
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon size={32} className="text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input
                      type="text"
                      value={formData.image}
                      onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                      className="w-full px-3 py-2 bg-background border border-border rounded text-sm"
                      placeholder="https://drive.google.com/file/d/..."
                    />
                    <p className="text-xs text-muted-foreground">
                      Paste a Google Drive share link. The image will be converted automatically.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={handleNameChange}
                    className="w-full px-3 py-2 bg-background border border-border rounded"
                    placeholder="Artist name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Slug</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded"
                    placeholder="artist-slug"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Role/Title</label>
                <input
                  type="text"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded"
                  placeholder="e.g., Filmmaker / Author"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Bio</label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded"
                  rows={3}
                  placeholder="Artist biography"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">IMDb URL</label>
                  <input
                    type="url"
                    value={formData.imdb}
                    onChange={(e) => setFormData({ ...formData, imdb: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded"
                    placeholder="https://imdb.com/name/..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Website</label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded"
                    placeholder="https://..."
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Admin: Project Affiliations with Checkboxes */}
          {user?.role === 'admin' && (
            <Card className="p-6">
              <h2 className="text-xl editorial-serif mb-4">Project Affiliations</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Check/uncheck to manage project affiliations.
              </p>
              {projects.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-2">
                  {projects.map((project) => (
                    <label 
                      key={project.project_id} 
                      className="flex items-center gap-3 p-3 border border-border rounded cursor-pointer hover:bg-muted/50"
                    >
                      <input
                        type="checkbox"
                        checked={formData.projects.includes(project.title)}
                        onChange={() => toggleProject(project.title)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">{project.title}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No projects available</p>
              )}
            </Card>
          )}

          {/* Admin: Company Affiliations with Checkboxes */}
          {user?.role === 'admin' && (
            <Card className="p-6">
              <h2 className="text-xl editorial-serif mb-4">Company Affiliations</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Check/uncheck to manage company affiliations.
              </p>
              {companies.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-2">
                  {companies.map((company) => (
                    <label 
                      key={company.company_id} 
                      className="flex items-center gap-3 p-3 border border-border rounded cursor-pointer hover:bg-muted/50"
                    >
                      <input
                        type="checkbox"
                        checked={formData.companies.includes(company.name)}
                        onChange={() => toggleCompany(company.name)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">{company.name}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No companies available</p>
              )}
            </Card>
          )}

          {/* Non-Admin: Current Associations (read-only) */}
          {user?.role !== 'admin' && !isNew && (
            <Card className="p-6">
              <h2 className="text-xl editorial-serif mb-4">Current Associations</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium mb-2">Projects</h3>
                  {formData.projects.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {formData.projects.map((project, idx) => (
                        <span key={idx} className="px-3 py-1 bg-primary/10 text-primary text-sm">
                          {project}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No projects yet</p>
                  )}
                </div>

                <div>
                  <h3 className="text-sm font-medium mb-2">Companies</h3>
                  {formData.companies.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {formData.companies.map((company, idx) => (
                        <span key={idx} className="px-3 py-1 bg-muted text-sm">
                          {company}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No companies yet</p>
                  )}
                </div>
              </div>
            </Card>
          )}

          {/* Non-Admin: Request Associations */}
          {user?.role !== 'admin' && !isNew && (
            <Card className="p-6">
              <h2 className="text-xl editorial-serif mb-4">Request to Join</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Request to join projects or companies. An admin will review your request.
              </p>
              
              <div className="space-y-6">
                {/* Request Project */}
                <div>
                  <h3 className="text-sm font-medium mb-2">Join a Project</h3>
                  {availableProjects.length > 0 ? (
                    <div className="grid md:grid-cols-2 gap-2">
                      {availableProjects.map((project) => (
                        <Button
                          key={project.project_id}
                          variant="outline"
                          size="sm"
                          className="justify-start"
                          onClick={() => handleRequestAssociation('project', project.project_id)}
                        >
                          <Plus size={14} className="mr-2" />
                          {project.title}
                        </Button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No available projects</p>
                  )}
                </div>

                {/* Request Company */}
                <div>
                  <h3 className="text-sm font-medium mb-2">Join a Company</h3>
                  {availableCompanies.length > 0 ? (
                    <div className="grid md:grid-cols-2 gap-2">
                      {availableCompanies.map((company) => (
                        <Button
                          key={company.company_id}
                          variant="outline"
                          size="sm"
                          className="justify-start"
                          onClick={() => handleRequestAssociation('company', company.company_id)}
                        >
                          <Plus size={14} className="mr-2" />
                          {company.name}
                        </Button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No available companies</p>
                  )}
                </div>
              </div>
            </Card>
          )}

          {/* Pending Requests (for non-admins) */}
          {user?.role !== 'admin' && !isNew && myRequests.length > 0 && (
            <Card className="p-6">
              <h2 className="text-xl editorial-serif mb-4">Your Requests</h2>
              <div className="space-y-3">
                {myRequests.map((req) => (
                  <div key={req.request_id} className="flex items-center justify-between p-3 bg-muted/50 rounded">
                    <div className="flex items-center gap-3">
                      {req.status === 'pending' && <Clock size={16} className="text-yellow-500" />}
                      {req.status === 'approved' && <CheckCircle size={16} className="text-green-500" />}
                      {req.status === 'rejected' && <XCircle size={16} className="text-red-500" />}
                      <div>
                        <p className="text-sm font-medium">{req.target_name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{req.target_type}</p>
                      </div>
                    </div>
                    <span className={`text-xs uppercase tracking-wider px-2 py-1 rounded ${
                      req.status === 'pending' ? 'bg-yellow-500/20 text-yellow-600' :
                      req.status === 'approved' ? 'bg-green-500/20 text-green-600' :
                      'bg-red-500/20 text-red-600'
                    }`}>
                      {req.status}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArtistEditorPage;
